/**
 * BossAnalytics - Statistiques Boss Ton Concours
 * Quiz, leçons, abonnements, leaderboard top 10
 */
import {
  BarChart3,
  BookOpen,
  CreditCard,
  Loader2,
  RefreshCw,
  Target,
  Trophy,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface BossAnalyticsData {
  quizzes_by_exam: Array<{ exam_name: string; total_attempts: number; avg_score: number }>;
  revenue_by_plan: Array<{ plan: string; count: number; estimated_revenue_xof: number }>;
  lesson_progress_7d: Array<{ subject: string; completions: number }>;
  leaderboard: Array<{ rank: number; full_name: string; total_points: number; current_streak: number }>;
  challenge_completion_rate: number;
  avg_quiz_score: number;
  total_revenue_estimate_xof: number;
}

const MiniBar = ({ label, value, max, unit = '' }: { label: string; value: number; max: number; unit?: string }) => (
  <div className="flex items-center gap-3 py-1.5">
    <span className="text-xs text-gray-600 w-36 truncate flex-shrink-0">{label}</span>
    <div className="flex-1 bg-gray-100 rounded-full h-2.5">
      <div
        className="bg-blue-400 h-2.5 rounded-full"
        style={{ width: max > 0 ? `${Math.min(100, (value / max) * 100)}%` : '0%' }}
      />
    </div>
    <span className="text-xs font-semibold text-gray-700 w-16 text-right">{value}{unit}</span>
  </div>
);

const BossAnalytics: React.FC = () => {
  const [data, setData] = useState<BossAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/boss/analytics`);
      if (!res.ok) throw new Error('Erreur chargement analytics Boss');
      setData(await res.json());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const d: BossAnalyticsData = data ?? {
    quizzes_by_exam: [],
    revenue_by_plan: [],
    lesson_progress_7d: [],
    leaderboard: [],
    challenge_completion_rate: 0,
    avg_quiz_score: 0,
    total_revenue_estimate_xof: 0,
  };

  const maxAttempts = Math.max(...d.quizzes_by_exam.map(e => e.total_attempts), 1);
  const maxLesson = Math.max(...d.lesson_progress_7d.map(l => l.completions), 1);
  const maxRevenue = Math.max(...d.revenue_by_plan.map(p => p.estimated_revenue_xof), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            <h1 className="text-2xl font-bold text-gray-900">Analytics Boss</h1>
          </div>
          <p className="text-gray-500 text-sm">Quiz par examen, leçons, revenus abonnements, classement</p>
        </div>
        <button onClick={loadData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
          ⚠️ {error} — créer <code className="bg-blue-100 px-1 rounded">/api/boss/analytics</code> dans le backend
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-blue-400" />
            <p className="text-xs text-gray-500">Score moyen quiz</p>
          </div>
          <p className="text-2xl font-bold">{d.avg_quiz_score}%</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-indigo-400" />
            <p className="text-xs text-gray-500">Challenges complétés (taux)</p>
          </div>
          <p className="text-2xl font-bold">{d.challenge_completion_rate}%</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-green-400" />
            <p className="text-xs text-gray-500">Revenus estimés (XOF)</p>
          </div>
          <p className="text-2xl font-bold">{d.total_revenue_estimate_xof.toLocaleString()}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Quiz par examen */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-blue-500" />
            <h3 className="font-semibold text-gray-800">Quiz par examen (tentatives)</h3>
          </div>
          {d.quizzes_by_exam.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Aucune donnée</p>
          ) : (
            d.quizzes_by_exam.slice(0, 8).map(e => (
              <MiniBar key={e.exam_name} label={e.exam_name} value={e.total_attempts} max={maxAttempts} />
            ))
          )}
        </div>

        {/* Leçons terminées 7j */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-indigo-500" />
            <h3 className="font-semibold text-gray-800">Leçons terminées — 7 jours</h3>
          </div>
          {d.lesson_progress_7d.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Aucune donnée</p>
          ) : (
            d.lesson_progress_7d.slice(0, 8).map(l => (
              <MiniBar key={l.subject} label={l.subject} value={l.completions} max={maxLesson} />
            ))
          )}
        </div>

        {/* Revenus par plan */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4 text-green-500" />
            <h3 className="font-semibold text-gray-800">Revenus estimés par plan (XOF)</h3>
          </div>
          {d.revenue_by_plan.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Aucune donnée</p>
          ) : (
            <div className="space-y-2">
              {d.revenue_by_plan.map(p => (
                <div key={p.plan} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <span className="text-sm font-medium capitalize">{p.plan}</span>
                    <span className="text-xs text-gray-400 ml-2">({p.count} abonnés)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-green-400 h-1.5 rounded-full"
                        style={{ width: `${Math.min(100, (p.estimated_revenue_xof / maxRevenue) * 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-green-700">{p.estimated_revenue_xof.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard top 10 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-amber-500" />
            <h3 className="font-semibold text-gray-800">Classement Top 10</h3>
          </div>
          {d.leaderboard.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Aucune donnée</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {d.leaderboard.map((entry) => (
                <div key={entry.rank} className="flex items-center gap-3 py-2">
                  <span className="text-sm font-bold text-gray-300 w-5">
                    {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : entry.rank}
                  </span>
                  <span className="flex-1 text-sm text-gray-900">{entry.full_name}</span>
                  <span className="text-xs text-orange-400">🔥{entry.current_streak}j</span>
                  <span className="text-sm font-bold text-blue-600">{entry.total_points.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Note technique */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700">
        <strong>Endpoints requis dans le backend :</strong>
        <ul className="mt-2 space-y-1 font-mono">
          <li>GET /api/boss/analytics — quiz par examen, leçons, revenus, leaderboard</li>
          <li>GET /api/boss/stats — KPIs généraux (overview)</li>
          <li>GET /api/boss/leaderboard?limit=10</li>
          <li>GET /api/boss/users?page&limit&search</li>
          <li>POST /api/boss/users/:id/promo — appliquer code promo</li>
          <li>DELETE /api/boss/users/:id/subscription — résilier abonnement</li>
        </ul>
      </div>
    </div>
  );
};

export default BossAnalytics;
